import React, { memo } from 'react'
import { useDispatch } from 'react-redux'
import { fields, query } from '@cbgms/components/list'
import {
  AccountType,
  CatalogStrategyType,
  CmsSystem,
  IOrderAccountData,
  OrderSystem,
  OrderSystemService,
  SystemAccountType,
  TicketSystem,
  VehicleSource
} from '@cbgms/api'
import { getOrderAccounts } from 'app/workorder/state/workorder/actions'
import { useDefaultOrderAccount } from 'app/order/components/Cart/useDefaultOrderAccount'
import { isEmptyUUID } from '@cbgms/base/utils/uuid'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import useQuery from '@cbgms/base/http/use-query'
import { getOrderAccountDetails } from 'app/settings/state/DefaultOrderAccount/actions'
import { LogContextProvider } from '../../../contexts/Logging/LogContext'
import { OrderAccountsContext, OrderAccountsContextValue } from './OrderAccountsContext'
import { CountryCode } from '@carsys/enums/country-code'

export interface OrderAccountData extends IOrderAccountData {
  UUID: IOrderAccountData['OrderAccount']['UUID']
  hasIFrameEnabled: boolean
  hasCatalogType: boolean
  fullName: string
}

const EmptyCatOrderAccount: OrderAccountData = {
  fullName: '',
  hasCatalogType: false,
  hasIFrameEnabled: false,
  UUID: '',
  OrderAccount: {
    AccountUUID: '',
    ShopID: '',
    UUID: '',
    UpdatedAt: '',
    CreatedAt: '',
    Credentials: {},
    DoNotShowOnList: true,
    WholesalerUUID: ''
  },
  BranchDetails: {
    BranchName: '',
    BranchEmail: '',
    BranchPhoneNumber: '',
    BranchFullAddress: '',
    Trips: []
  },
  Wholesaler: {
    AvailableForPortal: true,
    CMSSystem: CmsSystem.NA,
    Country: CountryCode.NL,
    CreatedAt: '',
    HaynesProDistributorUUID: '',
    HideLocalStock: false,
    HideTotalStock: false,
    IFrameURL: '',
    IsActive: false,
    LogoURL: '',
    Name: '',
    OrderEmailAcknowledgementWholesalerEmailAddress: '',
    OrderEmailAcknowledgementWholesalerEnabled: false,
    OrderSystem: OrderSystem.MijnGrossier,
    OrderSystemSettings: {},
    SupportEmail: '',
    SupportPhoneNumber: '',
    SupportFullAddress: '',
    TermsAndConditionsURL: '',
    TicketSystem: TicketSystem.NA,
    UpdatedAt: '',
    UserFields: [],
    UUID: ''
  },
  WholesalerOrderSystemServices: [],
  Catalog: {
    CatalogType: CatalogStrategyType.Hbase,
    UUID: '',
    CarsysProductTreeUUID: '',
    VehiclePrimaryVehicleSource: VehicleSource.CARSYS_HBASE,
    VPOMDashboardCatalogUUIDS: [],
    DashboardCatalogInfo: {
      ExternalPortals: [],
      Groups: [],
      OrderInPreparation: [],
      AddToOrderMenu: [],
      QuickNavigationProductGroup: []
    },
    VPOMHidePDHForNonTyres: false,
    VPOMUseDevelopmentAPI: false,
    WholesalerUUID: '',
    QuickNavToProductGroupUUIDS: []
  },
  Account: {
    BankBicNumber: '',
    BankIbanNumber: '',
    CocNumber: '',
    CompanyName: '',
    Department: '',
    EmailAddress: '',
    FirstName: '',
    IsClient: false,
    IsSupplier: false,
    Initials: '',
    MobileNumber: '',
    PhoneNumber: '',
    Name: '',
    NamePrefix: '',
    Title: '',
    Type: AccountType.Private,
    VatNumber: '',
    PostalAddress: '',
    PostalAddressExtra: '',
    PostalHouseNumber: '',
    PostalHouseNumberAddition: '',
    PostalPostalCode: '',
    PostalCountry: CountryCode.NL,
    PostalCity: '',
    PostalState: '',
    VisitorAddress: '',
    VisitorAddressExtra: '',
    VisitorHouseNumber: '',
    VisitorHouseNumberAddition: '',
    VisitorPostalCode: '',
    VisitorCountry: CountryCode.NL,
    VisitorCity: '',
    VisitorState: '',
    UpdatedAt: '',
    UUID: '',
    IsUseSageCashAccount: false,
    AccountNumber: '',
    CreatedAt: '',
    DateOfBirth: '',
    SystemAccountType: SystemAccountType.Standard
  }
}

const sortFunc = (a: OrderAccountData, b: OrderAccountData) => a.fullName.localeCompare(b.fullName)

type OrderAccountsCollections = Pick<OrderAccountsContextValue, 'orderAccountsWholesalerIFrame' | 'orderAccountsWholesalerCatalog'>

const decodePluspartsPassword = (orderAccount: IOrderAccountData): IOrderAccountData => {
  const password = orderAccount?.OrderAccount?.Credentials?.Password

  if (password && orderAccount?.Catalog?.DashboardCatalogInfo?.ExternalPortals) {
    const isPluspartsSelected = orderAccount.Catalog.DashboardCatalogInfo.ExternalPortals.some(
      portal => portal.Name === 'Plusparts' && portal.Selected
    )

    if (isPluspartsSelected) {
      try {
        const decoded = atob(password)
        if (decoded.endsWith('_Plusparts')) {
          return {
            ...orderAccount,
            OrderAccount: {
              ...orderAccount.OrderAccount,
              Credentials: {
                ...orderAccount.OrderAccount.Credentials,
                Password: decoded.slice(0, -'_Plusparts'.length)
              }
            }
          }
        }
      } catch (e) {
        // Fallback to original if not valid base64
      }
    }
  }

  return orderAccount
}

const mapOrderAccount = (orderAccount: IOrderAccountData) => ({
  ...orderAccount,
  UUID: orderAccount.OrderAccount.UUID,
  hasIFrameEnabled: !!orderAccount.WholesalerOrderSystemServices?.some(el => el.Service === OrderSystemService.IFrame && el.Active),
  hasCatalogType: !!orderAccount?.Catalog?.CatalogType && orderAccount.Catalog.CatalogType !== CatalogStrategyType.WholesalerGenArt,
  fullName: `${orderAccount.Account.CompanyName} ${orderAccount.OrderAccount.Credentials?.UserId}`
})

const getOrderAccountsCollections = (
  orderAccountsFromAPI: IOrderAccountData[],
  defaultOrderAccount: OrderAccountsContextValue['defaultOrderAccount']
): OrderAccountsCollections => {
  const mappedAccounts = orderAccountsFromAPI.map(oa => mapOrderAccount(oa))
  let orderAccountsWholesalerIFrame = mappedAccounts.filter(oa => oa.hasIFrameEnabled && oa.OrderAccount.UUID !== defaultOrderAccount?.UUID)
  let orderAccountsWholesalerCatalog = mappedAccounts.filter(oa => oa.hasCatalogType && oa.OrderAccount.UUID !== defaultOrderAccount?.UUID)

  orderAccountsWholesalerCatalog.sort(sortFunc)
  orderAccountsWholesalerIFrame.sort(sortFunc)

  if (defaultOrderAccount && !isEmptyUUID(defaultOrderAccount.UUID)) {
    // add the default order account to the top of the list(s)
    if (defaultOrderAccount.hasCatalogType) {
      orderAccountsWholesalerCatalog = [defaultOrderAccount, ...orderAccountsWholesalerCatalog]
    }
    if (defaultOrderAccount.hasIFrameEnabled) {
      orderAccountsWholesalerIFrame = [defaultOrderAccount, ...orderAccountsWholesalerIFrame]
    }
  }

  return {
    orderAccountsWholesalerIFrame,
    orderAccountsWholesalerCatalog
  }
}

interface OrderAccountProps {
  initialOrderAccountUUID?: string // this can be passed to set an selected order account. changing after initial render has no effect
}

const MemoOrderAccountsContextProvider: React.FC<{ value: OrderAccountsContextValue }> = memo(({ value, children }) => {
  return (
    <OrderAccountsContext.Provider value={value as OrderAccountsContextValue}>
      <LogContextProvider /* set a new LogContextProvider, the orderAccount is relevant and used in logging */>
        {children}
      </LogContextProvider>
    </OrderAccountsContext.Provider>
  )
})

export const OrderAccountsContextProvider: React.FC<OrderAccountProps> = ({ children, initialOrderAccountUUID }) => {
  const dispatch = useDispatch()
  const {
    orderAccount: defaultOrderAccountFromState,
    hasLoaded: defaultOrderAccountHasLoaded,
    loadDefaultOrderAccount
  } = useDefaultOrderAccount()

  const isPortal = useIsPortal()
  const [selectedOrderAccountUUID, setSelectedOrderAccountUUID] = React.useState<string>(initialOrderAccountUUID || '')

  const orderAccountsLoader = useQuery(
    ['order-accounts', 'context'],
    async () => {
      const params: { fields: string; search?: string; perPage: number } = {
        fields: fields<IOrderAccountData>()
          .add(oa => oa.Account.CompanyName)
          .add(oa => oa.OrderAccount.Credentials)
          .addAllOf(oa => oa.Wholesaler)
          .addAllOf(oa => oa.WholesalerOrderSystemServices)
          .addAllOf(oa => oa.Catalog)
          .toString(),
        perPage: 100
      }

      if (!isPortal) {
        params.search = query<IOrderAccountData>()
          .equals(oa => oa.OrderAccount.DoNotShowOnList, false)
          .toString()
      }

      const { Data } = await dispatch(getOrderAccounts(params))

      return Data ? Data.map(decodePluspartsPassword) : Data
    },
    { refetchOnWindowFocus: false }
  )

  const orderAccountDetailsLoader = useQuery(
    ['order-account-details', selectedOrderAccountUUID, 'with-related'],
    async () => {
      if (!selectedOrderAccountUUID) {
        return null
      }
      const { Data } = await dispatch(getOrderAccountDetails(selectedOrderAccountUUID))
      return Data ? decodePluspartsPassword(Data) : null
    },
    {
      // Use Redux data as initial data if it matches the selected UUID
      initialData: () => {
        const hasData = defaultOrderAccountFromState?.OrderAccount?.UUID === selectedOrderAccountUUID
        if (hasData) {
          return defaultOrderAccountFromState
        }
        return undefined
      },
      // Only fetch if we don't already have the data in Redux for this UUID
      enabled: (() => {
        const shouldEnable = !!selectedOrderAccountUUID && defaultOrderAccountFromState?.OrderAccount?.UUID !== selectedOrderAccountUUID
        return shouldEnable
      })(),
      refetchOnWindowFocus: false
    }
  )

  // Load default order account if it wasn't in state yet
  React.useEffect(() => {
    if (!defaultOrderAccountHasLoaded) {
      loadDefaultOrderAccount()
    }
  }, [defaultOrderAccountHasLoaded])

  const getOrderAccountInfo = React.useCallback(
    (orderAccountUUID: string | null): IOrderAccountData | null => {
      if (!orderAccountUUID) {
        return null
      }
      const orderAccount = orderAccountsLoader.data?.find(oa => oa.OrderAccount.UUID === orderAccountUUID)
      if (!orderAccount) {
        return null
      }
      return orderAccount
    },
    [orderAccountsLoader.data]
  )

  const value = React.useMemo((): OrderAccountsContextValue => {
    const defaultOrderAccount = defaultOrderAccountFromState ? mapOrderAccount(decodePluspartsPassword(defaultOrderAccountFromState)) : null

    const value: OrderAccountsContextValue = {
      hasBeenProvided: true,
      setSelectedOrderAccountUUID,
      selectedOrderAccount: defaultOrderAccount || EmptyCatOrderAccount,
      defaultOrderAccount,
      orderAccountsWholesalerCatalog: [] as OrderAccountsContextValue['orderAccountsWholesalerIFrame'],
      orderAccountsWholesalerIFrame: [] as OrderAccountsContextValue['orderAccountsWholesalerIFrame'],
      orderAccountsLoading: true,
      getOrderAccountInfo
    }

    if (!orderAccountsLoader.isLoading && !orderAccountsLoader.isError && defaultOrderAccountHasLoaded) {
      const selectedOrderAccountForProduct = localStorage.getItem('selectedOrderAccountForProduct')

      const finalSelectedOrderAccountUUID =
        isEmptyUUID(selectedOrderAccountUUID) && selectedOrderAccountForProduct
          ? selectedOrderAccountForProduct
          : isEmptyUUID(selectedOrderAccountUUID) && defaultOrderAccount?.UUID
          ? defaultOrderAccount.UUID
          : selectedOrderAccountUUID

      const { orderAccountsWholesalerCatalog, orderAccountsWholesalerIFrame } = getOrderAccountsCollections(
        orderAccountsLoader.data || [],
        defaultOrderAccount
      )

      const allOrderAccounts = [...orderAccountsWholesalerCatalog, ...orderAccountsWholesalerIFrame]
      if (defaultOrderAccount) {
        allOrderAccounts.push(defaultOrderAccount)
      }

      if (!isEmptyUUID(finalSelectedOrderAccountUUID)) {
        const selectedOrderAccount = allOrderAccounts.find(acc => acc && acc?.UUID === finalSelectedOrderAccountUUID)
        if (selectedOrderAccount) {
          value.selectedOrderAccount = selectedOrderAccount
        }
      }
      if (orderAccountDetailsLoader.data?.OrderAccount.UUID === finalSelectedOrderAccountUUID) {
        value.selectedOrderAccount.OrderAccount.DisplayRules = orderAccountDetailsLoader.data.OrderAccount.DisplayRules
      }

      value.displayRules = value.selectedOrderAccount.OrderAccount.DisplayRules
      value.orderAccountsWholesalerCatalog = orderAccountsWholesalerCatalog
      value.orderAccountsWholesalerIFrame = orderAccountsWholesalerIFrame
      value.orderAccountsLoading = false
    }

    return value
  }, [
    selectedOrderAccountUUID,
    orderAccountsLoader.isLoading,
    orderAccountsLoader.isError,
    defaultOrderAccountHasLoaded,
    defaultOrderAccountFromState?.OrderAccount?.UUID, // in case the default order account setting changes, we need to update the context
    orderAccountDetailsLoader.data,
    getOrderAccountInfo
  ])

  return <MemoOrderAccountsContextProvider value={value as OrderAccountsContextValue}>{children}</MemoOrderAccountsContextProvider>
}

export const { Consumer: OrderAccountsContextConsumer } = OrderAccountsContext
export { OrderAccountsContext }
